import {
    ArrowRight,
    CalendarClock,
    ChevronLeft,
    Clock3,
    Info,
    LoaderCircle,
    Lock,
    Settings2,
    Sparkles,
    ToggleLeft,
    ToggleRight,
    TriangleAlert,
    Unlock,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './CycleConfigEditScreen.module.css'

const FIELD_LABELS = {
    cycleType: 'Cycle frequency',
    cycleStartDay: 'Cycle start day',
    payDay: 'Pay day',
    payDayOffset: 'Pay month',
    lwpCalculationMethod: 'LWP method',
    halfDayCountsAs: 'Half day weight',
    gracePeriodMinutes: 'Grace period',
    lateDeductionEnabled: 'Late deduction',
    lateDeductionAfterCount: 'Free lates allowed',
    lateDeductionType: 'Late deduction type',
    lateDeductionAmount: 'Late deduction amount',
    autoLockOnPayDay: 'Auto-lock',
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
}

const normalizeConfig = (config) => {
    if (!config || typeof config !== 'object') return null
    return {
        cycleType: config.cycleType || 'MONTHLY',
        cycleStartDay: toNumber(config.cycleStartDay, 1),
        payDay: toNumber(config.payDay, 5),
        payDayOffset: config.payDayOffset || 'NEXT_MONTH',
        lwpCalculationMethod: config.lwpCalculationMethod || 'PER_WORKING_DAY',
        halfDayCountsAs: toNumber(config.halfDayCountsAs, 0.5),
        gracePeriodMinutes: toNumber(config.gracePeriodMinutes, 15),
        lateDeductionEnabled: toNumber(config.lateDeductionEnabled, 0),
        lateDeductionAfterCount: toNumber(config.lateDeductionAfterCount, 3),
        lateDeductionType: config.lateDeductionType || 'PER_LATE',
        lateDeductionAmount: toNumber(config.lateDeductionAmount, 0),
        autoLockOnPayDay: toNumber(config.autoLockOnPayDay, 0),
    }
}

const LwpPreview = ({ method }) => {
    const sampleSalary = 12000
    const calendarDays = 30
    const workingDays = 26
    const perDay =
        method === 'PER_WORKING_DAY'
            ? (sampleSalary / workingDays).toFixed(2)
            : (sampleSalary / calendarDays).toFixed(2)
    const divisor = method === 'PER_WORKING_DAY' ? `${workingDays} working days` : `${calendarDays} calendar days`

    return (
        <div className={styles.lwpPreviewCard}>
            <span className={styles.lwpPreviewTitle}>
                <Info size={12} /> Example
            </span>
            <p className={styles.lwpPreviewText}>
                ₹{sampleSalary.toLocaleString('en-IN')} salary, 1 LWP day →{' '}
                <strong>
                    ₹{sampleSalary.toLocaleString('en-IN')} ÷ {divisor} = ₹{perDay}/day deducted
                </strong>
            </p>
        </div>
    )
}

const toOrdinal = (value) => {
    const num = toNumber(value, 0)
    const rem10 = num % 10
    const rem100 = num % 100
    if (rem10 === 1 && rem100 !== 11) return `${num}st`
    if (rem10 === 2 && rem100 !== 12) return `${num}nd`
    if (rem10 === 3 && rem100 !== 13) return `${num}rd`
    return `${num}th`
}

const CycleConfigEditScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const routeConfig =
        location.state?.currentConfig ||
        location.state?.cycleConfig ||
        location.state?.config ||
        null

    const originalData = useMemo(() => normalizeConfig(routeConfig), [routeConfig])

    const [formData, setFormData] = useState(() => ({
        cycleType: originalData?.cycleType || 'MONTHLY',
        cycleStartDay: originalData?.cycleStartDay || 1,
        payDay: originalData?.payDay || 5,
        payDayOffset: originalData?.payDayOffset || 'NEXT_MONTH',
        lwpCalculationMethod: originalData?.lwpCalculationMethod || 'PER_WORKING_DAY',
        halfDayCountsAs: originalData?.halfDayCountsAs || 0.5,
        gracePeriodMinutes: originalData?.gracePeriodMinutes || 15,
        lateDeductionEnabled: (originalData?.lateDeductionEnabled || 0) === 1,
        lateDeductionAfterCount: originalData?.lateDeductionAfterCount || 3,
        lateDeductionType: originalData?.lateDeductionType || 'PER_LATE',
        lateDeductionAmount:
            (originalData?.lateDeductionAmount || 0) > 0
                ? String(originalData?.lateDeductionAmount || '')
                : '',
        autoLockOnPayDay: (originalData?.autoLockOnPayDay || 0) === 1,
    }))
    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        if (!originalData) return
        setFormData({
            cycleType: originalData.cycleType,
            cycleStartDay: originalData.cycleStartDay,
            payDay: originalData.payDay,
            payDayOffset: originalData.payDayOffset,
            lwpCalculationMethod: originalData.lwpCalculationMethod,
            halfDayCountsAs: originalData.halfDayCountsAs,
            gracePeriodMinutes: originalData.gracePeriodMinutes,
            lateDeductionEnabled: originalData.lateDeductionEnabled === 1,
            lateDeductionAfterCount: originalData.lateDeductionAfterCount,
            lateDeductionType: originalData.lateDeductionType,
            lateDeductionAmount:
                originalData.lateDeductionAmount > 0 ? String(originalData.lateDeductionAmount) : '',
            autoLockOnPayDay: originalData.autoLockOnPayDay === 1,
        })
        setErrors({})
    }, [originalData])

    const setField = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }))
        setErrors((prev) => {
            if (!prev[key]) return prev
            const next = { ...prev }
            delete next[key]
            return next
        })
    }

    const changedFields = useMemo(() => {
        if (!originalData) return []
        const changes = []
        if (formData.cycleType !== originalData.cycleType) changes.push('cycleType')
        if (formData.cycleStartDay !== originalData.cycleStartDay) changes.push('cycleStartDay')
        if (formData.payDay !== originalData.payDay) changes.push('payDay')
        if (formData.payDayOffset !== originalData.payDayOffset) changes.push('payDayOffset')
        if (formData.lwpCalculationMethod !== originalData.lwpCalculationMethod) changes.push('lwpCalculationMethod')
        if (Math.abs(formData.halfDayCountsAs - originalData.halfDayCountsAs) > 0.001) {
            changes.push('halfDayCountsAs')
        }
        if (formData.gracePeriodMinutes !== originalData.gracePeriodMinutes) changes.push('gracePeriodMinutes')
        if ((formData.lateDeductionEnabled ? 1 : 0) !== originalData.lateDeductionEnabled) {
            changes.push('lateDeductionEnabled')
        }
        if (formData.lateDeductionAfterCount !== originalData.lateDeductionAfterCount) {
            changes.push('lateDeductionAfterCount')
        }
        if (formData.lateDeductionType !== originalData.lateDeductionType) changes.push('lateDeductionType')
        if (Math.abs((parseFloat(formData.lateDeductionAmount) || 0) - originalData.lateDeductionAmount) > 0.001) {
            changes.push('lateDeductionAmount')
        }
        if ((formData.autoLockOnPayDay ? 1 : 0) !== originalData.autoLockOnPayDay) changes.push('autoLockOnPayDay')
        return changes
    }, [formData, originalData])

    const hasChanges = changedFields.length > 0
    const criticalImpact =
        changedFields.includes('cycleType') ||
        changedFields.includes('cycleStartDay') ||
        changedFields.includes('lwpCalculationMethod')

    const goBackSafely = useCallback(() => {
        if (!hasChanges || isSaving) {
            navigate(-1)
            return
        }
        const confirmed = window.confirm('You have unsaved changes. Leave without saving?')
        if (confirmed) {
            navigate(-1)
        }
    }, [hasChanges, isSaving, navigate])

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (!hasChanges || isSaving) return
            event.preventDefault()
            event.returnValue = ''
        }

        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [hasChanges, isSaving])

    const updateCycleConfig = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier || !originalData) return

        const newErrors = {}
        if (formData.cycleStartDay < 1 || formData.cycleStartDay > 28) {
            newErrors.cycleStartDay = 'Must be between 1 and 28.'
        }
        if (formData.payDay < 1 || formData.payDay > 28) {
            newErrors.payDay = 'Must be between 1 and 28.'
        }
        if (formData.gracePeriodMinutes < 0 || formData.gracePeriodMinutes > 60) {
            newErrors.gracePeriodMinutes = 'Must be between 0 and 60.'
        }
        if (formData.lateDeductionAfterCount < 0 || formData.lateDeductionAfterCount > 31) {
            newErrors.lateDeductionAfterCount = 'Must be between 0 and 31.'
        }
        if (
            formData.lateDeductionEnabled &&
            formData.lateDeductionType === 'PER_LATE' &&
            (!formData.lateDeductionAmount || parseFloat(formData.lateDeductionAmount) <= 0)
        ) {
            newErrors.lateDeductionAmount = 'Enter a deduction amount per late.'
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        if (!hasChanges) {
            toast.info('No changes to save.')
            return
        }

        setErrors({})

        try {
            setIsSaving(true)

            const payload = {
                token: authCtx.token,
                storeIdentifier: activeStore.storeIdentifier,
            }

            if (formData.cycleType !== originalData.cycleType) payload.cycleType = formData.cycleType
            if (formData.cycleStartDay !== originalData.cycleStartDay) payload.cycleStartDay = formData.cycleStartDay
            if (formData.payDay !== originalData.payDay) payload.payDay = formData.payDay
            if (formData.payDayOffset !== originalData.payDayOffset) payload.payDayOffset = formData.payDayOffset
            if (formData.lwpCalculationMethod !== originalData.lwpCalculationMethod) {
                payload.lwpCalculationMethod = formData.lwpCalculationMethod
            }
            if (Math.abs(formData.halfDayCountsAs - originalData.halfDayCountsAs) > 0.001) {
                payload.halfDayCountsAs = formData.halfDayCountsAs
            }
            if (formData.gracePeriodMinutes !== originalData.gracePeriodMinutes) {
                payload.gracePeriodMinutes = formData.gracePeriodMinutes
            }

            const newLateEnabled = formData.lateDeductionEnabled ? 1 : 0
            if (newLateEnabled !== originalData.lateDeductionEnabled) {
                payload.lateDeductionEnabled = newLateEnabled
            }

            if (newLateEnabled === 1) {
                if (formData.lateDeductionAfterCount !== originalData.lateDeductionAfterCount) {
                    payload.lateDeductionAfterCount = formData.lateDeductionAfterCount
                }
                if (formData.lateDeductionType !== originalData.lateDeductionType) {
                    payload.lateDeductionType = formData.lateDeductionType
                }
                const newAmt = parseFloat(formData.lateDeductionAmount) || 0
                if (Math.abs(newAmt - originalData.lateDeductionAmount) > 0.001) {
                    payload.lateDeductionAmount = newAmt
                }
            }

            const newAutoLock = formData.autoLockOnPayDay ? 1 : 0
            if (newAutoLock !== originalData.autoLockOnPayDay) {
                payload.autoLockOnPayDay = newAutoLock
            }

            const response = await fetch(BASE_URL_API + 'updateStoreSalaryCycleConfig', {
                method: 'POST',
                body: JSON.stringify(payload),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                if (Array.isArray(respo.response?.warnings)) {
                    respo.response.warnings.forEach((warning) => toast.warning(warning.message))
                }

                const count = respo.response?.changeCount ?? 1
                const changedFieldText = (respo.response?.changes || [])
                    .map((item) => FIELD_LABELS[item.field] || item.field)
                    .join(', ')

                toast.success(`${count} setting${count !== 1 ? 's' : ''} updated.`)
                if (changedFieldText) {
                    toast.info(`Updated: ${changedFieldText}`)
                }

                navigate(-1)
            } else {
                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                switch (respo?.errorCode) {
                    case 'CONF_002':
                        toast.error('No configuration found. Set it up first.')
                        navigate('/payroll/setup/cycle-config', { replace: true })
                        break

                    case 'VAL_008':
                        toast.info('No changes to save.')
                        break

                    case 'VAL_001':
                        setErrors((prev) => ({ ...prev, lateDeductionAmount: respo.message }))
                        break

                    case 'VAL_002':
                        if (respo.message?.includes('cycleStartDay')) {
                            setErrors((prev) => ({ ...prev, cycleStartDay: respo.message }))
                        } else if (respo.message?.includes('payDay')) {
                            setErrors((prev) => ({ ...prev, payDay: respo.message }))
                        } else if (respo.message?.includes('gracePeriodMinutes')) {
                            setErrors((prev) => ({ ...prev, gracePeriodMinutes: respo.message }))
                        } else if (respo.message?.includes('lateDeductionAfterCount')) {
                            setErrors((prev) => ({ ...prev, lateDeductionAfterCount: respo.message }))
                        } else {
                            toast.error(respo.message)
                        }
                        break

                    case 'VAL_003':
                        toast.error(respo.message)
                        break

                    default:
                        toast.error(respo?.message || 'Failed to save changes.')
                        break
                }
            }
        } catch (error) {
            console.error('updateStoreSalaryCycleConfig error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    if (!originalData) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Go back">
                        <ChevronLeft size={18} />
                    </button>
                    <div className={styles.headerMeta}>
                        <span className={styles.heroPill}>
                            <CalendarClock size={12} />
                            Payroll Setup
                        </span>
                        <h1 className={styles.headerTitle}>Edit Payroll Settings</h1>
                        <p className={styles.headerSub}>No existing configuration payload found for editing.</p>
                    </div>
                </div>

                <div className={styles.alertCard}>
                    <TriangleAlert size={16} />
                    <div>
                        <strong>Unable to prefill current configuration.</strong>
                        <p>Open this screen from the configuration view page, or create setup first.</p>
                    </div>
                </div>

                <button className={styles.saveBtn} onClick={() => navigate('/payroll/setup/cycle-config')}>
                    Go to Cycle Setup
                    <ArrowRight size={16} />
                </button>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={goBackSafely} aria-label="Go back">
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}>
                        <CalendarClock size={12} />
                        Payroll Setup
                    </span>
                    <h1 className={styles.headerTitle}>Edit Payroll Settings</h1>
                    <p className={styles.headerSub}>Adjust payroll cycle rules and save only what changed.</p>
                </div>
            </div>

            <div className={styles.formWrap}>
                {hasChanges && (
                    <div className={styles.changeBanner}>
                        <Sparkles size={14} />
                        <div>
                            <strong>
                                {changedFields.length} change{changedFields.length !== 1 ? 's' : ''} pending
                            </strong>
                            <p>
                                {changedFields.map((field) => FIELD_LABELS[field] || field).join(', ')}
                            </p>
                        </div>
                    </div>
                )}

                <div className={styles.sectionCard}>
                    <div className={styles.sectionHead}>
                        <CalendarClock size={15} />
                        Cycle Frequency
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Cycle Type <span className={styles.required}>*</span></label>
                        <div className={styles.segmentRow}>
                            {[
                                { value: 'MONTHLY', label: 'Monthly' },
                                { value: 'BIWEEKLY', label: 'Bi-weekly' },
                                { value: 'WEEKLY', label: 'Weekly' },
                            ].map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`${styles.segmentBtn} ${formData.cycleType === option.value ? styles.segmentBtnActive : ''}`}
                                    onClick={() => setField('cycleType', option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                        <p className={styles.fieldHint}>Most salons use Monthly</p>
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Cycle Start Day <span className={styles.required}>*</span></label>
                        <input
                            type="number"
                            min={1}
                            max={28}
                            className={`${styles.textInput} ${errors.cycleStartDay ? styles.inputError : ''}`}
                            value={formData.cycleStartDay}
                            onChange={(event) => setField('cycleStartDay', parseInt(event.target.value, 10) || 1)}
                        />
                        <p className={styles.fieldHint}>Day of month when attendance period begins (1–28)</p>
                        {errors.cycleStartDay && <p className={styles.fieldError}>{errors.cycleStartDay}</p>}
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Pay Day <span className={styles.required}>*</span></label>
                        <input
                            type="number"
                            min={1}
                            max={28}
                            className={`${styles.textInput} ${errors.payDay ? styles.inputError : ''}`}
                            value={formData.payDay}
                            onChange={(event) => setField('payDay', parseInt(event.target.value, 10) || 1)}
                        />
                        <p className={styles.fieldHint}>Day salaries are disbursed (1–28)</p>
                        {errors.payDay && <p className={styles.fieldError}>{errors.payDay}</p>}
                    </div>
                </div>

                <div className={styles.sectionCard}>
                    <div className={styles.sectionHead}>
                        <Settings2 size={15} />
                        Pay Date
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Pay Day Offset</label>
                        <div className={styles.offsetRow}>
                            {[
                                {
                                    value: 'NEXT_MONTH',
                                    label: 'Next Month',
                                    hint: 'April cycle → paid in May (recommended)',
                                    tag: 'Recommended',
                                },
                                {
                                    value: 'SAME_MONTH',
                                    label: 'Same Month',
                                    hint: 'April cycle → paid in April',
                                    tag: null,
                                },
                            ].map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`${styles.offsetCard} ${formData.payDayOffset === option.value ? styles.offsetCardActive : ''}`}
                                    onClick={() => setField('payDayOffset', option.value)}
                                >
                                    <span className={styles.offsetLabel}>
                                        {option.label}
                                        {option.tag && <span className={styles.offsetTag}>{option.tag}</span>}
                                    </span>
                                    <span className={styles.offsetHint}>{option.hint}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={styles.sectionCard}>
                    <div className={styles.sectionHead}>
                        <Settings2 size={15} />
                        LWP Calculation
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>LWP Method</label>
                        <div className={styles.offsetRow}>
                            {[
                                { value: 'PER_WORKING_DAY', label: 'Per Working Day', hint: 'Gross ÷ working days in month' },
                                { value: 'PER_CALENDAR_DAY', label: 'Per Calendar Day', hint: 'Gross ÷ calendar days in month' },
                            ].map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`${styles.offsetCard} ${formData.lwpCalculationMethod === option.value ? styles.offsetCardActive : ''}`}
                                    onClick={() => setField('lwpCalculationMethod', option.value)}
                                >
                                    <span className={styles.offsetLabel}>{option.label}</span>
                                    <span className={styles.offsetHint}>{option.hint}</span>
                                </button>
                            ))}
                        </div>
                        <LwpPreview method={formData.lwpCalculationMethod} />
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Half Day Counts As</label>
                        <div className={styles.segmentRow}>
                            {[
                                { value: 0.5, label: '0.5 days (standard)' },
                                { value: 1, label: 'Full day (lenient)' },
                            ].map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`${styles.segmentBtn} ${formData.halfDayCountsAs === option.value ? styles.segmentBtnActive : ''}`}
                                    onClick={() => setField('halfDayCountsAs', option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={styles.sectionCard}>
                    <div className={styles.sectionHead}>
                        <Clock3 size={15} />
                        Late Arrival
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Grace Period (minutes)</label>
                        <input
                            type="number"
                            min={0}
                            max={60}
                            className={`${styles.textInput} ${errors.gracePeriodMinutes ? styles.inputError : ''}`}
                            value={formData.gracePeriodMinutes}
                            onChange={(event) => setField('gracePeriodMinutes', parseInt(event.target.value, 10) || 0)}
                        />
                        <p className={styles.fieldHint}>Minutes after shift start before marking late (0–60)</p>
                        {errors.gracePeriodMinutes && <p className={styles.fieldError}>{errors.gracePeriodMinutes}</p>}
                    </div>

                    <div className={styles.toggleRow}>
                        <div className={styles.toggleMeta}>
                            <span className={styles.toggleLabel}>Late Deduction</span>
                            <span className={styles.toggleSub}>
                                Deduct salary for repeated late arrivals beyond the free allowance
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.toggleBtn}
                            onClick={() => setField('lateDeductionEnabled', !formData.lateDeductionEnabled)}
                            aria-label="Toggle late deduction"
                        >
                            {formData.lateDeductionEnabled ? (
                                <ToggleRight size={28} className={styles.toggleOn} />
                            ) : (
                                <ToggleLeft size={28} className={styles.toggleOff} />
                            )}
                        </button>
                    </div>

                    {formData.lateDeductionEnabled && (
                        <div className={styles.lateDeductionBlock}>
                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>Free Lates Allowed</label>
                                <input
                                    type="number"
                                    min={0}
                                    max={31}
                                    className={`${styles.textInput} ${errors.lateDeductionAfterCount ? styles.inputError : ''}`}
                                    value={formData.lateDeductionAfterCount}
                                    onChange={(event) =>
                                        setField('lateDeductionAfterCount', parseInt(event.target.value, 10) || 0)
                                    }
                                />
                                <p className={styles.fieldHint}>Number of lates allowed before deduction starts</p>
                                {errors.lateDeductionAfterCount && (
                                    <p className={styles.fieldError}>{errors.lateDeductionAfterCount}</p>
                                )}
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>Deduction Method</label>
                                <div className={styles.offsetRow}>
                                    {[
                                        {
                                            value: 'PER_LATE',
                                            label: 'Fixed amount per late',
                                            hint: 'Deduct ₹X for each late beyond the free allowance',
                                        },
                                        {
                                            value: 'HALF_DAY_PER_LATE',
                                            label: 'Half-day per late',
                                            hint: 'Each late counts as 0.5 LWP — auto-computed from daily rate',
                                        },
                                    ].map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            className={`${styles.offsetCard} ${formData.lateDeductionType === option.value ? styles.offsetCardActive : ''}`}
                                            onClick={() => setField('lateDeductionType', option.value)}
                                        >
                                            <span className={styles.offsetLabel}>{option.label}</span>
                                            <span className={styles.offsetHint}>{option.hint}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {formData.lateDeductionType === 'PER_LATE' && (
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>Amount Per Late (₹) <span className={styles.required}>*</span></label>
                                    <input
                                        type="number"
                                        min={0}
                                        className={`${styles.textInput} ${errors.lateDeductionAmount ? styles.inputError : ''}`}
                                        value={formData.lateDeductionAmount}
                                        placeholder="e.g. 100"
                                        onChange={(event) => setField('lateDeductionAmount', event.target.value)}
                                    />
                                    {errors.lateDeductionAmount && <p className={styles.fieldError}>{errors.lateDeductionAmount}</p>}
                                </div>
                            )}

                            {formData.lateDeductionType === 'HALF_DAY_PER_LATE' && (
                                <div className={styles.infoCard}>
                                    <Info size={13} />
                                    <p>
                                        Deduction is computed automatically from each staff member&apos;s effective daily
                                        rate. No fixed amount needed.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className={styles.sectionCard}>
                    <div className={styles.sectionHead}>
                        {formData.autoLockOnPayDay ? <Lock size={15} /> : <Unlock size={15} />}
                        Finalization
                    </div>

                    <div className={styles.toggleRow}>
                        <div className={styles.toggleMeta}>
                            <span className={styles.toggleLabel}>Auto-Lock on Pay Day</span>
                            <span className={styles.toggleSub}>
                                {formData.autoLockOnPayDay
                                    ? 'Payroll will be finalized automatically on the pay day.'
                                    : 'Manual review required before finalizing. Recommended for most salons.'}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.toggleBtn}
                            onClick={() => setField('autoLockOnPayDay', !formData.autoLockOnPayDay)}
                            aria-label="Toggle auto-lock"
                        >
                            {formData.autoLockOnPayDay ? (
                                <ToggleRight size={28} className={styles.toggleOn} />
                            ) : (
                                <ToggleLeft size={28} className={styles.toggleOff} />
                            )}
                        </button>
                    </div>

                    {!formData.autoLockOnPayDay && (
                        <div className={styles.infoCard}>
                            <Info size={13} />
                            <p>
                                Keeping auto-lock <strong>OFF</strong> gives you time to review attendance and
                                adjustments before salaries are disbursed.
                            </p>
                        </div>
                    )}
                </div>

                {criticalImpact && (
                    <div className={styles.impactNote}>
                        <TriangleAlert size={15} />
                        <p>
                            This change affects payroll computation rules. It takes effect from the next new payroll
                            run.
                        </p>
                    </div>
                )}

                <div className={styles.summaryCard}>
                    <div className={styles.summaryHead}>
                        <Sparkles size={13} />
                        Current Draft Summary
                    </div>
                    <div className={styles.summaryGrid}>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryKey}>Cycle</span>
                            <span className={styles.summaryVal}>
                                {formData.cycleType === 'MONTHLY'
                                    ? 'Monthly'
                                    : formData.cycleType === 'BIWEEKLY'
                                    ? 'Bi-weekly'
                                    : 'Weekly'}
                                , starts on {toOrdinal(formData.cycleStartDay)}
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryKey}>Pay Day</span>
                            <span className={styles.summaryVal}>
                                {toOrdinal(formData.payDay)} of{' '}
                                {formData.payDayOffset === 'NEXT_MONTH' ? 'following month' : 'same month'}
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryKey}>LWP Method</span>
                            <span className={styles.summaryVal}>
                                {formData.lwpCalculationMethod === 'PER_WORKING_DAY' ? 'Per working day' : 'Per calendar day'}
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryKey}>Late Deduction</span>
                            <span className={styles.summaryVal}>
                                {formData.lateDeductionEnabled
                                    ? formData.lateDeductionType === 'HALF_DAY_PER_LATE'
                                        ? `½-day/late after ${formData.lateDeductionAfterCount} free`
                                        : `₹${formData.lateDeductionAmount || 0}/late after ${formData.lateDeductionAfterCount} free`
                                    : 'Disabled'}
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryKey}>Auto-Lock</span>
                            <span className={styles.summaryVal}>{formData.autoLockOnPayDay ? 'Enabled' : 'Manual (recommended)'}</span>
                        </div>
                    </div>
                </div>

                <button
                    className={`${styles.saveBtn} ${(isSaving || !hasChanges) ? styles.saveBtnDisabled : ''}`}
                    disabled={isSaving || !hasChanges}
                    onClick={updateCycleConfig}
                >
                    {isSaving ? (
                        <>
                            <LoaderCircle size={16} className={styles.spin} />
                            Saving...
                        </>
                    ) : (
                        <>
                            Save Changes
                            <ArrowRight size={16} />
                        </>
                    )}
                </button>
            </div>
        </div>
    )
}

export default CycleConfigEditScreen